import { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import type { AlertListResponse } from "@/api/types";

// Normalised shape consumed by the UI
interface AlertItem {
  id: string;
  date: string;
  districtId: number;
  districtName: string;
  region: string;
  riskLevel: "High" | "Medium" | "Low";
  growthRate: number;
  recommendedAction: string;
  status: "Active" | "Acknowledged" | "Resolved";
  aiReasoning: string;
  contributingFactors: string; // backend returns a single string
}

function capitalise(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;
}

function normalise(raw: AlertListResponse["items"][number]): AlertItem {
  return {
    id: String(raw.id),
    date: raw.created_at ? raw.created_at.split("T")[0] : "—",
    districtId: raw.district_id,
    districtName: raw.district_name ?? `District ${raw.district_id}`,
    region: raw.district_region ?? "—",
    // Backend risk_level/status are lowercase: high|medium|low and active|resolved
    riskLevel: capitalise(raw.risk_level) as AlertItem["riskLevel"],
    growthRate: raw.predicted_increase_pct,
    // UI expects the recommended action text
    recommendedAction: raw.recommended_action ?? "—",
    status: capitalise(raw.status) as AlertItem["status"],

    aiReasoning: raw.ai_reasoning ?? "—",
    contributingFactors: raw.contributing_factors ?? "—",
  };
}

type SortField = "date" | "districtName" | "riskLevel" | "growthRate" | "status";
type SortDirection = "asc" | "desc";

interface FilterState {
  region: string;
  severity: string;
  status: string;
  dateFrom: string;
  dateTo: string;
  search: string;
}

function getRiskPriority(level: string) {
  return level === "High" ? 3 : level === "Medium" ? 2 : 1;
}

function getStatusPriority(status: string) {
  return status === "Active" ? 3 : status === "Acknowledged" ? 2 : 1;
}

export default function Alerts() {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [allAlerts, setAllAlerts] = useState<AlertItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [sortField, setSortField] = useState<SortField>("riskLevel");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [filters, setFilters] = useState<FilterState>({
    region: "",
    severity: "",
    status: "",
    dateFrom: "",
    dateTo: "",
    search: "",
  });
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [exportToast, setExportToast] = useState(false);

  // Fetch all alerts once (up to 500; server handles filtering later if needed)
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        // Backend requires: per_page/page; other filters are optional but must match backend parameter names.
        const data = await api.alerts({ page: 1, per_page: 50 }) as AlertListResponse;

        if (!cancelled) {
          const items = (data?.items ?? []).map(normalise);
          setAllAlerts(items);
          setTotalCount(data?.total ?? items.length);
        }
      } catch {
        if (!cancelled) setAllAlerts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Derive unique regions from loaded alerts
  const regions = useMemo(
    () => Array.from(new Set(allAlerts.map((a) => a.region).filter((r) => r !== "—"))).sort(),
    [allAlerts]
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir(field === "riskLevel" ? "desc" : "asc");
    }
  };

  const sortedFiltered = useMemo(() => {
    let data = [...allAlerts];

    if (filters.region) data = data.filter((a) => a.region === filters.region);
    if (filters.severity) data = data.filter((a) => a.riskLevel === filters.severity);
    if (filters.status) data = data.filter((a) => a.status === filters.status);
    if (filters.dateFrom) data = data.filter((a) => a.date >= filters.dateFrom);
    if (filters.dateTo) data = data.filter((a) => a.date <= filters.dateTo);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      data = data.filter(
        (a) =>
          a.districtName.toLowerCase().includes(q) ||
          a.id.toLowerCase().includes(q) ||
          a.recommendedAction.toLowerCase().includes(q)
      );
    }

    data.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case "date":
          cmp = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case "districtName":
          cmp = a.districtName.localeCompare(b.districtName);
          break;
        case "riskLevel":
          cmp = getRiskPriority(a.riskLevel) - getRiskPriority(b.riskLevel);
          break;
        case "growthRate":
          cmp = a.growthRate - b.growthRate;
          break;
        case "status":
          cmp = getStatusPriority(a.status) - getStatusPriority(b.status);
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

    return data;
  }, [sortField, sortDir, filters, allAlerts]);

  const toggleExpand = useCallback(
    (id: string) => {
      setExpandedRow(expandedRow === id ? null : id);
    },
    [expandedRow]
  );

  const handleExport = () => {
    const csv = [
      ["ID", "Date", "District", "Region", "Risk Level", "Growth Rate", "Recommended Action", "Status"].join(","),
      ...sortedFiltered.map((a) =>
        [a.id, a.date, `"${a.districtName}"`, a.region, a.riskLevel, `${a.growthRate}%`, `"${a.recommendedAction}"`, a.status].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `malariaguard-alerts-${new Date().toISOString().split("T")[0]}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
    setExportToast(true);
    setTimeout(() => setExportToast(false), 2500);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <i className="ri-arrow-up-down-line text-[10px] text-slate-400 dark:text-slate-600 ml-1" />;
    return (
      <i
        className={`text-[10px] ml-1 ${
          sortDir === "asc" ? "ri-arrow-up-line text-teal" : "ri-arrow-down-line text-teal"
        }`}
      />
    );
  };

  const activeFilterCount = Object.values(filters).filter((v) => v !== "").length;

  const riskBadgeStyles: Record<string, string> = {
    High: "bg-risk-high/15 text-risk-high border-risk-high/20",
    Medium: "bg-amber/15 text-amber border-amber/20",
    Low: "bg-risk-low/15 text-risk-low border-risk-low/20",
  };

  const statusBadgeStyles: Record<string, string> = {
    Active: "bg-red-500/15 text-red-500 dark:text-red-400 border-red-500/20",
    Acknowledged: "bg-amber/15 text-amber border-amber/20",
    Resolved: "bg-risk-low/15 text-risk-low border-risk-low/20",
  };

  const riskLabel = (level: string) => {
    if (level === "High") return t("common_high");
    if (level === "Medium") return t("common_medium");
    return t("common_low");
  };

  const statusLabel = (status: string) => {
    if (status === "Active") return t("common_active");
    if (status === "Acknowledged") return t("common_acknowledged");
    return t("common_resolved");
  };

  return (
    <div className="animate-fade-in space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-display font-bold text-slate-900 dark:text-white">{t("alerts_title")}</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t("alerts_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-colors ${
              showFilters || activeFilterCount > 0
                ? "bg-teal-muted/30 border-teal/20 text-teal"
                : "bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            <i className="ri-filter-3-line" />
            {t("alerts_filters")}
            {activeFilterCount > 0 && (
              <span className="ml-1 w-4 h-4 rounded-full bg-teal text-white text-[10px] font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-download-line" />
            {t("alerts_export_csv")}
          </button>
        </div>
      </div>

      {exportToast && (
        <div className="fixed bottom-5 right-5 z-50 animate-slide-up">
          <div className="bg-teal-muted/80 backdrop-blur-sm border border-teal/30 rounded-lg px-4 py-3 flex items-center gap-2 shadow-glow">
            <i className="ri-check-line text-teal" />
            <span className="text-sm text-teal">{t("reports_exported")}</span>
          </div>
        </div>
      )}

      {showFilters && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4 space-y-3 animate-slide-up">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider font-medium mb-1 block">
                {t("alerts_region")}
              </label>
              <select
                value={filters.region}
                onChange={(e) => setFilters({ ...filters, region: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:border-teal/50"
              >
                <option value="">{t("alerts_all_regions")}</option>
                {regions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider font-medium mb-1 block">
                {t("alerts_severity")}
              </label>
              <select
                value={filters.severity}
                onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:border-teal/50"
              >
                <option value="">{t("alerts_all_severities")}</option>
                <option value="High">{t("common_high")}</option>
                <option value="Medium">{t("common_medium")}</option>
                <option value="Low">{t("common_low")}</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider font-medium mb-1 block">
                {t("alerts_status")}
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:border-teal/50"
              >
                <option value="">{t("alerts_all_statuses")}</option>
                <option value="Active">{t("common_active")}</option>
                <option value="Acknowledged">{t("common_acknowledged")}</option>
                <option value="Resolved">{t("common_resolved")}</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider font-medium mb-1 block">
                {t("alerts_date_from")}
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:border-teal/50"
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider font-medium mb-1 block">
                {t("alerts_date_to")}
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:border-teal/50"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                placeholder={t("alerts_search")}
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-teal/50"
              />
            </div>
            {activeFilterCount > 0 && (
              <button
                onClick={() =>
                  setFilters({ region: "", severity: "", status: "", dateFrom: "", dateTo: "", search: "" })
                }
                className="px-3 py-2 rounded-lg text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              >
                {t("common_reset")}
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="min-h-[200px] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-slate-700 border-t-teal rounded-full animate-spin" />
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    <button onClick={() => handleSort("date")} className="flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">
                      Date <SortIcon field="date" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    <button onClick={() => handleSort("districtName")} className="flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">
                      {t("reports_district")} <SortIcon field="districtName" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    <button onClick={() => handleSort("riskLevel")} className="flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">
                      {t("alerts_risk")} <SortIcon field="riskLevel" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    <button onClick={() => handleSort("growthRate")} className="flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">
                      {t("alerts_growth")} <SortIcon field="growthRate" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    {t("alerts_recommended_action")}
                  </th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    <button onClick={() => handleSort("status")} className="flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-300 transition-colors">
                      {t("alerts_status_label")} <SortIcon field="status" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider w-12"></th>
                </tr>
              </thead>
              <tbody>
                {sortedFiltered.map((alert) => {
                  const isExpanded = expandedRow === alert.id;
                  return (
                    <>
                      <tr
                        key={alert.id}
                        className={`border-b border-slate-200/50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors cursor-pointer ${
                          isExpanded ? "bg-slate-50 dark:bg-slate-800/30" : ""
                        }`}
                        onClick={() => toggleExpand(alert.id)}
                      >
                        <td className="px-4 py-3 text-xs text-slate-700 dark:text-slate-300 font-mono">{alert.date}</td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-xs font-medium text-slate-800 dark:text-slate-200">{alert.districtName}</p>
                            <p className="text-[10px] text-slate-500 dark:text-slate-500">{alert.region}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-medium border ${riskBadgeStyles[alert.riskLevel] ?? ""}`}>
                            {riskLabel(alert.riskLevel)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-mono font-medium ${
                            alert.growthRate > 30 ? "text-risk-high" : alert.growthRate > 10 ? "text-amber" : "text-risk-low"
                          }`}>
                            {alert.growthRate > 0 ? "+" : ""}{alert.growthRate}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400 max-w-[200px] truncate">{alert.recommendedAction}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-medium border ${statusBadgeStyles[alert.status] ?? ""}`}>
                            {statusLabel(alert.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <i className={`ri-arrow-down-s-line text-sm text-slate-400 dark:text-slate-500 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr key={`${alert.id}-expanded`} className="bg-slate-50 dark:bg-slate-900/60">
                          <td colSpan={7} className="px-4 py-4 animate-fade-in">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <h4 className="text-[10px] font-semibold text-teal uppercase tracking-wider flex items-center gap-1.5">
                                  <i className="ri-brain-line" />
                                  {t("alerts_ai_reasoning")}
                                </h4>
                                <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">{alert.aiReasoning}</p>
                              </div>
                              <div className="space-y-2">
                                <h4 className="text-[10px] font-semibold text-teal uppercase tracking-wider flex items-center gap-1.5">
                                  <i className="ri-checkbox-multiple-line" />
                                  {t("alerts_recommended_action")}
                                </h4>
                                <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">{alert.recommendedAction}</p>
                              </div>
                              <div className="space-y-2">
                                <h4 className="text-[10px] font-semibold text-amber uppercase tracking-wider flex items-center gap-1.5">
                                  <i className="ri-list-check" />
                                  {t("alerts_contributing_factors")}
                                </h4>
                                {/* Backend field is stored as a single string; support common separators */}
                                {(() => {
                                  const raw = alert.contributingFactors ?? "";
                                  const parts = raw
                                    .split(/\r?\n|•|\s*;\s*|\s*,\s*/g)
                                    .map((s) => s.trim())
                                    .filter(Boolean);
                                  if (parts.length <= 1) {
                                    return (
                                      <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">{raw || "—"}</p>
                                    );
                                  }
                                  return (
                                    <ul className="space-y-1">
                                      {parts.map((p, i) => (
                                        <li key={i} className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-300">
                                          <span className="w-1 h-1 rounded-full bg-amber mt-1.5 flex-shrink-0" />
                                          {p}
                                        </li>
                                      ))}
                                    </ul>
                                  );
                                })()}
                              </div>


                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-slate-200 dark:divide-slate-800">
            {sortedFiltered.map((alert) => {
              const isExpanded = expandedRow === alert.id;
              return (
                <div key={alert.id} className="animate-fade-in">
                  <div className="p-4 space-y-3 cursor-pointer" onClick={() => toggleExpand(alert.id)}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">{alert.id}</span>
                      <span className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">{alert.date}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{alert.districtName}</p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-500">{alert.region}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-medium border ${riskBadgeStyles[alert.riskLevel] ?? ""}`}>
                        {riskLabel(alert.riskLevel)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-[10px] text-slate-500 dark:text-slate-500">{t("alerts_growth")}</p>
                        <p className={`text-sm font-mono font-medium ${alert.growthRate > 30 ? "text-risk-high" : alert.growthRate > 10 ? "text-amber" : "text-risk-low"}`}>
                          {alert.growthRate > 0 ? "+" : ""}{alert.growthRate}%
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 dark:text-slate-500">{t("alerts_status_label")}</p>
                        <p className="text-xs text-slate-700 dark:text-slate-300">{statusLabel(alert.status)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{alert.recommendedAction}</p>
                    <div className="flex items-center justify-center pt-1">
                      <i className={`ri-arrow-down-s-line text-sm text-slate-400 dark:text-slate-500 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-4 animate-fade-in bg-slate-50 dark:bg-slate-900/40">
                      <div className="space-y-2">
                        <h4 className="text-[10px] font-semibold text-teal uppercase tracking-wider flex items-center gap-1.5">
                          <i className="ri-brain-line" />
                          {t("alerts_ai_reasoning")}
                        </h4>
                        <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">{alert.aiReasoning}</p>
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-[10px] font-semibold text-amber uppercase tracking-wider flex items-center gap-1.5">
                          <i className="ri-list-check" />
                          {t("alerts_contributing_factors")}
                        </h4>
                        <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">{alert.contributingFactors}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {sortedFiltered.length === 0 && !loading && (
            <div className="p-10 text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 mx-auto mb-3">
                <i className="ri-inbox-line text-xl text-slate-400 dark:text-slate-500" />
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t("alerts_no_match")}</p>
              <p className="text-xs text-slate-400 dark:text-slate-600 mt-1">{t("alerts_try_adjusting")}</p>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-500">
        <span>
          {t("alerts_showing", { count: sortedFiltered.length, total: totalCount })}
        </span>
      </div>
    </div>
  );
}
